import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import type {Block} from '@/services/kontent-ai/types'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {Card as ClCard} from '@/products/flexible-web-toolkit/components/card/index'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {ReusableCTAButtonComponent} from '../cta-button'

export type CardContentItem = IContentItem<{
	image: Elements.AssetsElement
	title: Elements.TextElement
	text: Elements.TextElement
	buttonText: Elements.TextElement
	buttonLink: Elements.TextElement
	openInNewTab: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	variant: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
}>

export const CardBlock: Block<CardContentItem> = ({
	block,
	page,
	...context
}) => {
	const {colorId: finalColorId} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.variant,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	const textLines = block.elements.text
		.split(/\n/)
		.filter((element) => element)
	const spacingChoice =
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		]
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, spacingChoice may be null
	const componentSpacing = !spacingChoice ? spacing.none : spacingChoice

	return (
		<ClCard
			className={clsx(
				'h-full break-words text-black sm:!w-[21rem] xxs:!max-w-[18rem]',
				componentSpacing
			)}
			href={null}
			title={
				<span data-kontent-element-codename="card_title">
					{block.elements.title}
				</span>
			}
		>
			{block.elements.image[0] ? (
				<CardImage
					alt={block.elements.image[0]?.description}
					className="h-48 object-cover"
					data-kontent-element-codename="image"
					src={block.elements.image[0]?.url}
				/>
			) : null}
			{textLines.length > 0 && (
				<div data-kontent-element-codename="card_text">
					{textLines.map((text, index) => (
						<p
							className={
								textLines.length - 1 !== index ? 'mb-3' : ''
							}
							key={text}
						>
							{text}
						</p>
					))}
				</div>
			)}
			{block.elements.buttonText ? (
				<CardFooter>
					<ReusableCTAButtonComponent
						block={{
							elements: {
								icon: [],
								openLinkInNewTab: block.elements.openInNewTab,
								preserveQueryString: [],
								text: block.elements.buttonText,
								url: block.elements.buttonLink,
								variant: block.elements.variant,
								brandColor: finalColorId ?? '',
								authenticationGateComponentUserRoles: [],
								snippetSpacingSpacing: [],
							},
							system: {
								codename: '',
								id: '',
								type: 'cta_button',
								name: '',
							},
						}}
						className="w-full"
						ctaButtonOrigin={`${block.elements.title} card`} // used for analytics to identify which card download button the click came from
						data-kontent-element-codename="button_text"
						page={page}
						{...context}
					/>
				</CardFooter>
			) : null}
		</ClCard>
	)
}
